import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Icon from "../AppIcon";
import { APP_ROUTES } from "utils/appRoutes";
import MasterUploadModal from "components/MasterUploadModal";
import GlobalCombinedUploadModal from "components/ui/GlobalUploadModal";

const getInitials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) ?? "";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    profile,
    signOut,
    memberships,
    selectedOrg,
    switchOrganization,
    refreshProfile,
  } = useAuth();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState(null);

  const [showMasterUploadModal, setShowMasterUploadModal] = useState(false);
  const [showGlobalUploadModal, setShowGlobalUploadModal] = useState(false);

  const orgSwitcherRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        orgSwitcherRef.current &&
        !orgSwitcherRef.current.contains(e.target)
      ) {
        setShowOrgSwitcher(false);
      }
    };
    if (showOrgSwitcher) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showOrgSwitcher]);

  const allowedRoutes = JSON.parse(
    localStorage.getItem("allowedRoutes") || "[]",
  );

  const isAdmin =
    profile?.role === "admin" ||
    profile?.is_admin === true ||
    allowedRoutes.includes(APP_ROUTES.GLOBAL_UPLOAD);

  const handleSignOut = async () => {
    try {
      setIsLoggingOut(true);
      await signOut();
      navigate("/user-login");
    } catch (err) {
      console.error("Sign out error:", err);
    } finally {
      setIsLoggingOut(false);
      setShowUserMenu(false);
    }
  };

  const handleSwitchOrg = async (membership) => {
    if (switchingOrgId) return;
    if (membership.orgId === selectedOrg?.orgId) {
      setShowOrgSwitcher(false);
      return;
    }
    setSwitchingOrgId(membership.orgId);
    try {
      await switchOrganization(membership);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Org switch failed:", err);
    } finally {
      setSwitchingOrgId(null);
      setShowOrgSwitcher(false);
    }
  };

  if (!user) return null;

  const hiddenHeaderRoutes = ["/verify-otp", "/security/mfa", "/org-selection"];
  if (hiddenHeaderRoutes.includes(location.pathname)) return null;

  const isSettingsPage = location.pathname.includes(
    "/group-organization-management",
  );
  const displayName =
    profile?.fullName || user?.email?.split("@")?.[0] || "User";
  const hasMultipleOrgs = memberships.length > 1;

  return (
    <header className="bg-card border-b border-border z-40 shadow-sm flex-shrink-0">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* ── Left: Sidebar trigger + Org Switcher ── */}
          {/* <div className="flex items-center gap-4">
           
            {selectedOrg && (
              <div className="relative" ref={orgSwitcherRef}>
                <button
                  onClick={() =>
                    hasMultipleOrgs && setShowOrgSwitcher((v) => !v)
                  }
                  className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium
                    border border-border bg-muted/40 text-foreground
                    transition-all duration-200
                    ${
                      hasMultipleOrgs
                        ? "hover:bg-muted hover:border-border/80 cursor-pointer"
                        : "cursor-default"
                    }
                  `}
                  title={
                    hasMultipleOrgs
                      ? "Switch organization"
                      : selectedOrg.orgName
                  }
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                  <span className="max-w-[160px] truncate hidden md:inline">
                    {selectedOrg.orgName}
                  </span>
                  <span className="inline md:hidden">
                    <Icon name="Building2" size={14} />
                  </span>
                  {selectedOrg.categoryName && (
                    <span className="hidden lg:inline text-xs text-muted-foreground border-l border-border/60 pl-2">
                      {selectedOrg.categoryName}
                    </span>
                  )}
                  {hasMultipleOrgs && (
                    <Icon
                      name={showOrgSwitcher ? "ChevronUp" : "ChevronDown"}
                      size={14}
                      className="text-muted-foreground flex-shrink-0"
                    />
                  )}
                </button>

                {hasMultipleOrgs && showOrgSwitcher && (
                  <div className="absolute left-0 top-full mt-2 w-72 bg-card border border-border rounded-xl shadow-xl z-50 py-2 overflow-hidden">
                    <div className="px-4 py-2 border-b border-border">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Switch Organization
                      </p>
                    </div>

                    <div className="max-h-64 overflow-y-auto">
                      {memberships.map((m) => {
                        const isActive = m.orgId === selectedOrg?.orgId;
                        const isSwitching = switchingOrgId === m.orgId;

                        return (
                          <button
                            key={m.orgId}
                            onClick={() => handleSwitchOrg(m)}
                            disabled={!!switchingOrgId}
                            className={`
                              w-full px-4 py-3 flex items-center gap-3 text-left
                              transition-colors duration-150
                              ${
                                isActive
                                  ? "bg-primary/8 text-foreground cursor-default"
                                  : "text-foreground hover:bg-muted/50 cursor-pointer"
                              }
                              disabled:opacity-60
                            `}
                          >
                            <div
                              className={`
                              w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                              ${isActive ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}
                            `}
                            >
                              {isSwitching ? (
                                <div className="w-4 h-4 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                              ) : (
                                <Icon name="Building2" size={16} />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {m.orgName}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {m.categoryName}
                              </p>
                            </div>

                            {isActive && (
                              <Icon
                                name="Check"
                                size={16}
                                className="text-primary flex-shrink-0"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div> */}

          {/* ── Right: User Menu ── */}
          <div className="flex items-center justify-end w-full gap-3">
            <div className="relative">
              {/* ✅ Avatar button with initials */}
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                  {profile?.avatarUrl ? (
                    <img
                      src={profile.avatarUrl}
                      alt={displayName}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : getInitials(displayName) ? (
                    <span className="text-white text-xs font-semibold">
                      {getInitials(displayName)}
                    </span>
                  ) : (
                    <Icon name="User" size={18} className="text-white" />
                  )}
                </div>
                <Icon
                  name={showUserMenu ? "ChevronUp" : "ChevronDown"}
                  size={16}
                  className="text-muted-foreground"
                />
              </button>

              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-50 py-2">
                    {/* User info */}
                    <div className="px-4 py-3 border-b border-border">
                      <div className="flex items-center gap-3 mb-1">
                        {/* Larger initials avatar inside dropdown */}
                        <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                          {profile?.avatarUrl ? (
                            <img
                              src={profile.avatarUrl}
                              alt={displayName}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-white text-sm font-semibold">
                              {getInitials(displayName)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-foreground truncate">
                              {displayName}
                            </p>
                            {isAdmin && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700">
                                ADMIN
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {user?.email}
                          </p>
                        </div>
                      </div>
                      {selectedOrg && (
                        <div className="flex items-center gap-1.5 mt-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          <p className="text-xs text-muted-foreground">
                            {selectedOrg.orgName}
                          </p>
                          <span className="text-xs text-muted-foreground/50">
                            ·
                          </span>
                          <p className="text-xs text-muted-foreground">
                            {selectedOrg.categoryName}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Menu items */}
                    <div className="py-2">
                      <button
                        onClick={() => {
                          navigate("/dashboard");
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 transition-colors flex items-center gap-2"
                      >
                        <Icon name="LayoutDashboard" size={16} />
                        Dashboard
                      </button>
                      <button
                        onClick={() => {
                          navigate("/personnel-profile-management");
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 transition-colors flex items-center gap-2"
                      >
                        <Icon name="Users" size={16} />
                        Team Profiles
                      </button>

                      {selectedOrg?.categoryId === 3 && (
                        <button
                          onClick={() => setShowMasterUploadModal(true)}
                          disabled={!!selectedOrg?.firstBulkUpload}
                          className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors
                            disabled:opacity-40 disabled:cursor-not-allowed
                            enabled:text-foreground enabled:hover:bg-muted/50"
                          title={
                            selectedOrg?.firstBulkUpload
                              ? "Masters & Personnel already uploaded"
                              : "Upload masters & personnel"
                          }
                        >
                          <Icon name="FileSpreadsheet" size={18} />
                          <span>Masters & Personnel Upload</span>
                        </button>
                      )}

                      {selectedOrg?.categoryId === 3 && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowGlobalUploadModal(true);
                          }}
                          className="w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2"
                        >
                          <Icon name="Globe" size={16} />
                          <span>Global Upload</span>
                        </button>
                      )}

                      {hasMultipleOrgs && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate("/org-selection");
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 transition-colors flex items-center gap-2"
                        >
                          <Icon name="ArrowLeftRight" size={16} />
                          Switch Organization
                        </button>
                      )}
                    </div>

                    {/* Sign out */}
                    <div className="border-t border-border pt-2">
                      <button
                        onClick={handleSignOut}
                        disabled={isLoggingOut}
                        className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        <Icon name="LogOut" size={16} />
                        {isLoggingOut ? "Signing Out..." : "Sign Out"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <MasterUploadModal
        open={showMasterUploadModal}
        onClose={() => setShowMasterUploadModal(false)}
        onSuccess={async () => {
          await refreshProfile?.();
          setShowMasterUploadModal(false);
        }}
        orgId={selectedOrg?.orgId || profile?.organization_id}
      />

      <GlobalCombinedUploadModal
        open={showGlobalUploadModal}
        onClose={() => setShowGlobalUploadModal(false)}
        orgId={selectedOrg?.orgId || profile?.organization_id}
      />
    </header>
  );
};

export default Header;
